/*
 * AdminCentralDemissionalTab
 * -------------------------
 * Central Demissional — geração e arquivamento do "Término de Contrato de
 * Trabalho". Dois botões principais:
 *   1. Gerar novo comunicado  — seleciona funcionário + informa data
 *   2. Upload de PDF assinado — anexa o mesmo termo assinado manualmente
 *
 * Layout inspirado no fluxo do Kit Admissional (busca + tabela + preview
 * inline).
 */
import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  UserMinus, Search, Sparkles, Upload, Eye, Trash2, Loader2, X,
  CheckCircle2, Circle, Clock, FileText, Calendar, User, MapPin,
  FileUp, Paperclip, Package, Download, RefreshCw,
} from "lucide-react";

function fmtBr(d) {
  if (!d) return "—";
  const s = String(d).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, dd] = s.split("-");
    return `${dd}/${m}/${y}`;
  }
  return s;
}

// ---------- Create dialog: search & pick employee, inform data ----------
const VERBAS_FIELDS = [
  { key: "saldo_salario", label: "Saldo de Salário", group: "creditos" },
  { key: "ferias_proporcionais", label: "Férias Proporcionais", group: "creditos" },
  { key: "ferias_vencidas", label: "Férias Vencidas", group: "creditos" },
  { key: "terco_ferias", label: "Terço Constitucional", group: "creditos" },
  { key: "decimo_terceiro", label: "13º Salário Rescisão", group: "creditos" },
  { key: "outras", label: "Outras verbas", group: "creditos" },
  { key: "desconto_vt", label: "Vale-Transporte", group: "descontos" },
  { key: "desconto_inss", label: "Previdência Social (INSS)", group: "descontos" },
  { key: "desconto_inss_13", label: "Previd. Social 13º", group: "descontos" },
  { key: "desconto_irrf", label: "IRRF", group: "descontos" },
  { key: "desconto_outros", label: "Outros Descontos", group: "descontos" },
  { key: "desconto_pensao", label: "Pensão Alimentícia", group: "descontos" },
];

// -------------------- Bulk create from spreadsheet --------------------
function BulkTerminoPanel({ onDone }) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [dataDesl, setDataDesl] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file) {
      toast.error("Escolha um arquivo XLSX ou CSV.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (dataDesl) fd.append("data_desligamento", dataDesl);
      fd.append("only_comunicado", "true");
      const r = await axios.post(
        `${API}/admin/termino-contrato/bulk-create`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        },
      );
      onDone?.(r.data || { created_count: 0, skipped_count: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-md border-2 border-dashed border-orange-200 bg-orange-50/40 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <FileUp className="w-4 h-4 text-orange-600" />
        <div className="text-xs font-semibold text-orange-900">
          Gerar em lote (planilha)
        </div>
      </div>
      <p className="text-[11px] text-slate-600 leading-snug">
        Envie XLSX ou CSV com colunas <b>Matrícula</b>, <b>CPF</b>,{" "}
        <b>Nome</b>. Coluna <b>Data Desligamento</b> é opcional — se
        vazia, aplica a data abaixo.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          ref={inputRef}
          type="file"
          accept=".xlsx,.csv,.txt"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-xs bg-white"
          data-testid="bulk-termino-file"
        />
        <Input
          type="date"
          value={dataDesl}
          onChange={(e) => setDataDesl(e.target.value)}
          className="w-full sm:w-40 text-xs bg-white"
          placeholder="Data padrão"
          data-testid="bulk-termino-date"
        />
        <Button
          size="sm"
          onClick={upload}
          disabled={sending || !file}
          className="bg-orange-600 hover:bg-orange-700 whitespace-nowrap"
          data-testid="bulk-termino-submit"
        >
          {sending
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Gerando…</>
            : <><Upload className="w-3.5 h-3.5 mr-1.5" />Gerar em lote</>}
        </Button>
      </div>
    </div>
  );
}


// -------------------- Kit Demissional (attach after create) --------------------
function KitDemissionalDialog({ open, rec, onClose, onUpdated }) {
  const { token } = useAuth();
  const [docFile, setDocFile] = useState(null);
  const [docKind, setDocKind] = useState("outro");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingKit, setUploadingKit] = useState(false);
  const [kitFile, setKitFile] = useState(null);
  const [kitResult, setKitResult] = useState(null);
  const [previewNonce, setPreviewNonce] = useState(0);
  const docRef = useRef(null);
  const kitRef = useRef(null);

  // Build the iframe src straight from the endpoint (with ?token= for
  // auth) and a nonce query param so the iframe refetches whenever the
  // record's underlying documents change. This avoids the browser
  // blocking blob: URLs inside iframes (some Chromium sandboxes
  // reject them, leaving an empty preview).
  const previewNonceKey = rec
    ? [rec.has_trct, rec.has_protocolo, rec.has_carta_ref,
       rec.kit_uploaded_at, rec.protocolo_generated_at,
       rec.carta_ref_generated_at, rec.has_signed_pdf,
       (rec.attachments || []).length, previewNonce].join("|")
    : "";
  const hasAnyPiece = rec && (rec.has_trct || rec.has_protocolo
    || rec.has_carta_ref || rec.kit_uploaded_at
    || rec.has_signed_pdf);
  const kitPreviewUrl = rec && hasAnyPiece && token
    ? `${API}/admin/termino-contrato/${rec.id}/preview-kit`
      + `?token=${encodeURIComponent(token)}`
      + `&_n=${encodeURIComponent(previewNonceKey)}`
      + `#toolbar=1&navpanes=0`
    : null;

  const reloadKitPreview = () => setPreviewNonce((n) => n + 1);

  if (!rec) return null;

  const uploadKitFull = async () => {
    if (!kitFile) {
      toast.error("Escolha o PDF do Kit Demissional.");
      return;
    }
    setUploadingKit(true);
    setKitResult(null);
    try {
      const fd = new FormData();
      fd.append("file", kitFile);
      const r = await axios.post(
        `${API}/admin/termino-contrato/${rec.id}/upload-kit-full`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 180000,
        },
      );
      setKitResult(r.data);
      toast.success(
        `Kit processado — ${r.data.total_pages} páginas · ` +
        `TRCT ${r.data.trct ? "✓" : "—"} · ` +
        `Carta ${r.data.carta ? "✓" : "—"} · ` +
        `Protocolo ${r.data.protocolo ? "✓" : "—"} · ` +
        `Preservadas ${r.data.external_count}`,
      );
      setKitFile(null);
      if (kitRef.current) kitRef.current.value = "";
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload do kit.");
    } finally {
      setUploadingKit(false);
    }
  };

  const openBlobPdf = (path) => {
    (async () => {
      try {
        const r = await axios.get(`${API}${path}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });
        const url = URL.createObjectURL(r.data);
        window.open(url, "_blank");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Falha ao abrir.");
      }
    })();
  };
  const openProtocolo = () =>
    openBlobPdf(`/admin/termino-contrato/${rec.id}/protocolo`);
  const openCartaRef = () =>
    openBlobPdf(`/admin/termino-contrato/${rec.id}/carta-referencia`);
  const openKitCompleto = () =>
    openBlobPdf(`/admin/termino-contrato/${rec.id}/preview-kit`);

  const uploadDoc = async () => {
    if (!docFile) {
      toast.error("Escolha um PDF.");
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("kind", docKind || "outro");
      await axios.post(
        `${API}/admin/termino-contrato/${rec.id}/attach-doc`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        },
      );
      toast.success("Documento anexado.");
      setDocFile(null);
      if (docRef.current) docRef.current.value = "";
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao anexar.");
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteAttachment = async (attId) => {
    if (!window.confirm("Remover este anexo?")) return;
    try {
      await axios.delete(
        `${API}/admin/termino-contrato/${rec.id}/attachment/${attId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Anexo removido.");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover.");
    }
  };

  const openAttachment = (attId) => {
    window.open(
      `${API}/admin/termino-contrato/${rec.id}/attachment/${attId}?token=${encodeURIComponent(token)}`,
      "_blank",
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl w-[95vw] h-[92vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-100 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-4 h-4 text-orange-600" />
            Kit Demissional — {rec.employee_nome}
          </DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">
            {rec.doc_number} · Matr. {rec.employee_matricula} · Desligamento{" "}
            {fmtBr(rec.data_desligamento)}
          </div>
        </DialogHeader>

        {/* Two-column layout: LEFT = inline PDF preview of the full kit
            (Comunicado → TRCT → preservadas → Carta → Protocolo);
            RIGHT = actions (upload/gerar/anexar). */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          {/* Inline preview panel */}
          <div className="bg-slate-100 border-r border-slate-200 flex flex-col min-h-0">
            <div className="px-3 py-2 flex items-center gap-2 text-[11px] text-slate-600 bg-white border-b border-slate-200">
              <Eye className="w-3.5 h-3.5 text-slate-500" />
              <span className="font-medium">Preview do Kit</span>
              <span className="text-slate-400">
                — mesma ordem que o colaborador verá
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={reloadKitPreview}
                  className="p-1 hover:bg-slate-100 rounded"
                  title="Atualizar preview"
                  data-testid="kit-preview-refresh"
                >
                  <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
                </button>
                {kitPreviewUrl && (
                  <a
                    href={kitPreviewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1 hover:bg-slate-100 rounded"
                    title="Abrir em nova aba"
                    data-testid="kit-preview-newtab"
                  >
                    <FileText className="w-3.5 h-3.5 text-slate-500" />
                  </a>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-slate-300">
              {kitPreviewUrl ? (
                <iframe
                  key={kitPreviewUrl}
                  src={kitPreviewUrl}
                  className="w-full h-full border-0 bg-white"
                  title="Preview do Kit Demissional"
                  data-testid="kit-preview-iframe"
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-xs text-slate-500 gap-2 p-6 text-center">
                  <Package className="w-8 h-8 text-slate-400" />
                  <div>Nenhum documento gerado ainda.</div>
                  <div className="text-slate-400">
                    Envie o Kit ou gere as peças ao lado — o preview
                    aparece aqui automaticamente.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions panel */}
          <div className="overflow-y-auto p-4">

        <div className="space-y-5">
          {/* One-shot upload of the full Kit PDF */}
          <div className="rounded-md border-2 border-dashed border-orange-300 bg-orange-50/40 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-600" />
              <div className="text-sm font-semibold text-orange-900">
                Upload do Kit Completo (recomendado)
              </div>
              {rec.kit_uploaded_at && (
                <Badge variant="outline" className="border-orange-300 text-orange-700 text-[10px]">
                  Enviado
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-slate-600 leading-snug">
              Envie o PDF completo do Kit (do sistema da folha). O
              sistema identifica automaticamente cada página: <b>TRCT</b>,{" "}
              <b>Carta</b> e <b>Protocolo</b> são regenerados no padrão
              AVI; comprovantes bancários e Guia FGTS são preservados
              como estão. A Carta é assinada só pela empresa.
            </p>
            <div className="flex gap-2">
              <Input
                ref={kitRef}
                type="file"
                accept=".pdf"
                onChange={(e) => setKitFile(e.target.files?.[0] || null)}
                className="text-xs bg-white"
                data-testid="kit-full-file"
              />
              <Button
                size="sm"
                onClick={uploadKitFull}
                disabled={!kitFile || uploadingKit}
                className="bg-orange-600 hover:bg-orange-700 whitespace-nowrap"
                data-testid="kit-full-submit"
              >
                {uploadingKit
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Processando…</>
                  : <><Upload className="w-3.5 h-3.5 mr-1.5" />Processar Kit</>}
              </Button>
            </div>
            {(kitResult || rec.kit_page_map?.length > 0) && (
              <div className="mt-2 bg-white/70 rounded p-2 text-[11px] text-slate-700 space-y-1">
                <div className="font-semibold text-slate-600">
                  Mapa de páginas do kit ({rec.kit_page_map?.length ||
                    kitResult?.total_pages} páginas):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(kitResult?.page_map || rec.kit_page_map || []).map((label, i) => {
                    const c =
                      label === "trct" ? "bg-blue-100 text-blue-800" :
                      label === "carta" ? "bg-amber-100 text-amber-800" :
                      label === "protocolo" ? "bg-emerald-100 text-emerald-800" :
                      "bg-slate-100 text-slate-600";
                    const txt =
                      label === "trct" ? "TRCT" :
                      label === "carta" ? "Carta" :
                      label === "protocolo" ? "Protocolo" :
                      "Preservada";
                    return (
                      <span key={i} className={`px-2 py-0.5 rounded ${c} text-[10px] font-mono`}>
                        pg{i+1} · {txt}
                      </span>
                    );
                  })}
                </div>
                {rec.kit_external_page_count > 0 && (
                  <div className="text-[10px] text-slate-500">
                    {rec.kit_external_page_count} página(s) preservada(s)
                    exatamente como no arquivo original (comprovantes /
                    FGTS).
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Status do Kit — read-only checklist (TRCT / Protocolo / Carta) */}
          <div className="rounded-md border border-slate-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <div className="text-sm font-semibold text-slate-800">
                Peças do Kit
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              Estas peças são geradas automaticamente pelo <b>Upload do Kit
              Completo</b> acima. Aqui você acompanha o status e visualiza o
              PDF gerado no padrão AVI.
            </p>
            <div className="space-y-1.5" data-testid="kit-status-checklist">
              {[
                {
                  key: "trct",
                  label: "TRCT + Homologação",
                  iconClass: "text-blue-600",
                  attached: !!rec.has_trct,
                  view: null,
                },
                {
                  key: "protocolo",
                  label: "Protocolo de Kit Demissional",
                  iconClass: "text-emerald-600",
                  attached: !!rec.has_protocolo,
                  view: openProtocolo,
                  testid: "kit-protocolo-view",
                },
                {
                  key: "carta",
                  label: "Carta de Referência",
                  iconClass: "text-amber-600",
                  attached: !!rec.has_carta_ref,
                  view: openCartaRef,
                  testid: "kit-carta-ref-view",
                },
              ].map((it) => (
                <div
                  key={it.key}
                  className="flex items-center gap-2 rounded bg-slate-50 px-2.5 py-2"
                  data-testid={`kit-status-row-${it.key}`}
                >
                  {it.attached ? (
                    <CheckCircle2 className={`w-4 h-4 shrink-0 ${it.iconClass}`} />
                  ) : (
                    <Circle className="w-4 h-4 text-slate-300 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-800 truncate">
                      {it.label}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {it.attached ? "Anexado no padrão AVI" : "Pendente — envie o Kit Completo"}
                    </div>
                  </div>
                  {it.attached && it.view && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={it.view}
                      className="h-7 px-2 text-[11px] whitespace-nowrap"
                      data-testid={it.testid}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      Ver
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Outros documentos */}
          <div className="rounded-md border border-slate-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-violet-600" />
              <div className="text-sm font-semibold text-slate-800">
                Outros Documentos (PDF)
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              Anexe qualquer PDF adicional que compõe o kit
              (ex: ficha de ponto, aviso prévio, carta homologação).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2">
              <Input
                ref={docRef}
                type="file"
                accept=".pdf"
                onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="kit-doc-file"
              />
              <Input
                value={docKind}
                onChange={(e) => setDocKind(e.target.value)}
                placeholder="Tipo (ex: ponto)"
                className="text-xs"
                data-testid="kit-doc-kind"
              />
            </div>
            <Button
              size="sm"
              onClick={uploadDoc}
              disabled={!docFile || uploadingDoc}
              className="bg-violet-600 hover:bg-violet-700 w-full sm:w-auto"
              data-testid="kit-doc-submit"
            >
              {uploadingDoc
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Enviando…</>
                : <><Upload className="w-3.5 h-3.5 mr-1.5" />Anexar documento</>}
            </Button>

            {rec.attachments?.length > 0 && (
              <div className="mt-2 border-t border-slate-100 pt-2 space-y-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  Anexos ({rec.attachments.length})
                </div>
                {rec.attachments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5"
                  >
                    <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{a.filename}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {a.kind} · {(a.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button
                      onClick={() => openAttachment(a.id)}
                      className="p-1 hover:bg-slate-200 rounded"
                      title="Visualizar"
                      data-testid={`kit-att-view-${a.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 text-slate-600" />
                    </button>
                    <button
                      onClick={() => deleteAttachment(a.id)}
                      className="p-1 hover:bg-red-100 rounded"
                      title="Remover"
                      data-testid={`kit-att-del-${a.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-100 shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}
                  data-testid="kit-close-btn">
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function CreateTerminoDialog({ open, onClose, onCreated }) {
  const { token } = useAuth();
  const [step, setStep] = useState(1);  // 1 = pick emp, 2 = date, 3 = verbas
  const [search, setSearch] = useState("");
  const [emps, setEmps] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [dataDesligamento, setDataDesligamento] = useState("");
  const [verbas, setVerbas] = useState({});
  const [extraFields, setExtraFields] = useState({});  // parsed employee/contract data
  const [creating, setCreating] = useState(false);
  const [parsing, setParsing] = useState(false);
  const searchDebounce = useRef(null);
  const folhaFileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSearch("");
    setEmps([]);
    setPicked(null);
    setDataDesligamento("");
    setVerbas({});
    setExtraFields({});
  }, [open]);

  // Auto-compute totals whenever any field changes
  // Parses BOTH BR-format strings ("1.234,56") AND raw JS numbers /
  // US-format strings ("1234.56") that come straight from the parser.
  const toNumber = (v) => {
    if (v == null || v === "") return 0;
    if (typeof v === "number") return isNaN(v) ? 0 : v;
    const s = String(v).trim();
    // BR format has a comma decimal separator — strip thousand dots first.
    const n = s.includes(",")
      ? parseFloat(s.replace(/\./g, "").replace(",", "."))
      : parseFloat(s);
    return isNaN(n) ? 0 : n;
  };
  const totals = useMemo(() => {
    const cred = VERBAS_FIELDS
      .filter((f) => f.group === "creditos")
      .reduce((s, f) => s + toNumber(verbas[f.key]), 0);
    const desc = VERBAS_FIELDS
      .filter((f) => f.group === "descontos")
      .reduce((s, f) => s + toNumber(verbas[f.key]), 0);
    return { bruto: cred, deducoes: desc, liquido: cred - desc };
  }, [verbas]);

  const setV = (k, v) => setVerbas((p) => ({ ...p, [k]: v }));
  const fmtBrl = (v) =>
    v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Search employees by name/matricula (debounced)
  useEffect(() => {
    if (!open || step !== 1) return;
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!search.trim()) { setEmps([]); return; }
    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/admin/employees`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: search.trim(), page: 1, per_page: 20 },
          timeout: 15000,
        });
        setEmps(r.data?.employees || []);
      } catch {
        setEmps([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [search, open, step, token]);

  const parseFolhamatic = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Envie um PDF do Folhamatic.");
      return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/admin/termino-contrato/parse-folhamatic`,
        fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 60000,
        });
      const d = r.data?.data || {};
      // Pre-fill verbas — parser returns raw floats (US format), convert
      // them to BR-format strings ("1.234,56") so the user sees / edits
      // values in the local convention.
      const toBr = (n) => {
        if (n == null || n === "") return "";
        const num = typeof n === "number" ? n : parseFloat(n);
        if (isNaN(num)) return "";
        return num.toLocaleString("pt-BR", {
          minimumFractionDigits: 2, maximumFractionDigits: 2,
        });
      };
      const v = d.verbas || {};
      setVerbas({
        saldo_salario: toBr(v.saldo_salario),
        ferias_proporcionais: toBr(v.ferias_proporcionais),
        ferias_vencidas: toBr(v.ferias_vencidas),
        terco_ferias: toBr(v.terco_ferias),
        decimo_terceiro: toBr(v.decimo_terceiro),
        outras: toBr(v.outras),
        desconto_vt: toBr(v.desconto_vt),
        desconto_inss: toBr(v.desconto_inss),
        desconto_inss_13: toBr(v.desconto_inss_13),
        desconto_irrf: toBr(v.desconto_irrf),
        desconto_outros: toBr(v.desconto_outros),
        desconto_pensao: toBr(v.desconto_pensao),
      });
      // Save employee/contract extras to be sent alongside verbas
      setExtraFields({
        pis: d.pis, ctps: d.ctps, nascimento: d.nascimento,
        mae: d.mae, endereco: d.endereco, bairro: d.bairro,
        cidade: d.cidade, estado: d.estado, cep: d.cep,
        admissao: d.admissao, categoria: d.categoria,
        salario: d.salario,
        causa_afastamento: d.causa_afastamento,
        codigo_afastamento: d.codigo_afastamento,
        data_aviso_previo: d.data_aviso_previo,
      });
      // If Folhamatic provides an afastamento date and we don't have one
      // typed yet, use it.
      if (!dataDesligamento && d.data_afastamento) {
        const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(d.data_afastamento);
        if (m) setDataDesligamento(`${m[3]}-${m[2]}-${m[1]}`);
      }
      const filledCount = Object.values(v).filter(
        (x) => x != null && x !== "" && x !== 0,
      ).length;
      toast.success(`${filledCount} valor(es) extraído(s) do PDF. Revise antes de gerar.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui ler o PDF.");
    } finally {
      setParsing(false);
    }
  };

  const submit = async ({ onlyComunicado = false } = {}) => {
    if (!picked?.id) return;
    if (!dataDesligamento) {
      toast.error("Informe a data de desligamento.");
      return;
    }
    setCreating(true);
    // Same dual-format tolerance as `toNumber` — accept BR ("1.234,56")
    // or raw JS numbers / US ("1234.56") coming from the parser.
    const parseNum = (v) => {
      if (v === "" || v == null) return null;
      if (typeof v === "number") return isNaN(v) ? null : v;
      const s = String(v).trim();
      const n = s.includes(",")
        ? parseFloat(s.replace(/\./g, "").replace(",", "."))
        : parseFloat(s);
      return isNaN(n) ? null : n;
    };
    const verbasPayload = {};
    if (!onlyComunicado) {
      VERBAS_FIELDS.forEach((f) => {
        const n = parseNum(verbas[f.key]);
        if (n != null) verbasPayload[f.key] = n;
      });
      if (Object.keys(verbasPayload).length > 0) {
        verbasPayload.total_bruto = totals.bruto;
        verbasPayload.total_deducoes = totals.deducoes;
        verbasPayload.valor_liquido = totals.liquido;
      }
    }
    try {
      const r = await axios.post(
        `${API}/admin/termino-contrato/create`,
        {
          employee_id: picked.id,
          data_desligamento: dataDesligamento,
          verbas: verbasPayload,
          overrides: onlyComunicado ? {} : extraFields,
          only_comunicado: !!onlyComunicado,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );
      toast.success(
        onlyComunicado
          ? `Comunicado ${r.data?.record?.doc_number} gerado! Anexe o TRCT/docs depois.`
          : `Comunicado ${r.data?.record?.doc_number} gerado!`,
      );
      onCreated?.(r.data?.record);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className={step === 3 ? "max-w-2xl" : "max-w-lg"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-orange-600" />
            Novo Comunicado de Desligamento
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Passo 1 de 3 — Selecione o funcionário ou envie uma planilha
            </div>

            {/* Bulk-create from spreadsheet */}
            <BulkTerminoPanel
              onDone={(res) => {
                toast.success(
                  `${res.created_count} comunicado(s) gerados. ` +
                  (res.skipped_count
                    ? `${res.skipped_count} pulados.`
                    : ""),
                );
                onCreated?.(null);
                onClose?.();
              }}
            />

            <div className="relative flex items-center py-1">
              <div className="flex-1 border-t border-slate-200" />
              <span className="mx-3 text-[10px] uppercase tracking-widest text-slate-400">
                ou 1 por vez
              </span>
              <div className="flex-1 border-t border-slate-200" />
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome ou matrícula…"
                className="pl-9"
                data-testid="termino-create-search"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
              )}
            </div>
            <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
              {emps.length === 0 && search && !searching ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  Nenhum funcionário encontrado.
                </div>
              ) : emps.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  Digite pelo menos 2 caracteres para buscar.
                </div>
              ) : emps.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => { setPicked(e); setStep(2); }}
                  className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm flex items-center justify-between gap-2"
                  data-testid={`termino-emp-${e.matricula}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{e.nome}</div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      Matr. {e.matricula} · CPF {e.cpf_masked || "—"}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && picked && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Passo 2 de 3 — Informe a data do desligamento
            </div>
            <div className="rounded-md bg-orange-50 border border-orange-200 p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-orange-700" />
                <span className="font-medium">{picked.nome}</span>
              </div>
              <div className="text-[11px] text-slate-600 font-mono">
                Matrícula {picked.matricula} · {picked.posto_servico || "sem posto"}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                Data do desligamento
              </label>
              <Input
                type="date"
                value={dataDesligamento}
                onChange={(e) => setDataDesligamento(e.target.value)}
                data-testid="termino-create-date"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                O exame demissional será agendado automaticamente para o
                próximo dia útil após esta data.
              </p>
            </div>
          </div>
        )}

        {step === 3 && picked && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Passo 3 de 3 — Verbas rescisórias
            </div>

            {/* Folhamatic upload — extracts everything automatically */}
            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-indigo-800 flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" />
                    Preencher automaticamente com PDF do Folhamatic
                  </div>
                  <p className="text-[11px] text-indigo-700/80 mt-0.5">
                    Envie o TRCT do Folhamatic e eu extraio todos os valores,
                    cadastro do funcionário e dados contratuais.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => folhaFileRef.current?.click()}
                  disabled={parsing}
                  className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs"
                  data-testid="termino-folhamatic-upload"
                >
                  {parsing
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Lendo…</>
                    : <><Upload className="w-3.5 h-3.5 mr-1.5" />Escolher PDF</>}
                </Button>
                <input
                  ref={folhaFileRef} type="file" accept="application/pdf"
                  className="hidden" onChange={parseFolhamatic}
                />
              </div>
              {Object.keys(extraFields).some((k) => extraFields[k]) && (
                <div className="mt-2 text-[10.5px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                  <CheckCircle2 className="w-3 h-3 inline mr-1" />
                  Dados do PDF detectados. O TRCT vai usar essas informações
                  no lugar do cadastro local.
                </div>
              )}
            </div>

            <div className="text-[11px] text-slate-500">
              Ou digite manualmente os valores abaixo. Deixe em branco para
              gerar o TRCT com linhas vazias.
            </div>

            <div>
              <div className="text-xs font-bold text-emerald-700 mb-1.5 uppercase tracking-wide">
                Créditos
              </div>
              <div className="grid grid-cols-2 gap-2">
                {VERBAS_FIELDS.filter((f) => f.group === "creditos").map((f) => (
                  <label key={f.key} className="text-[11px] text-slate-600">
                    {f.label}
                    <div className="relative mt-0.5">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">R$</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0,00"
                        value={verbas[f.key] || ""}
                        onChange={(e) => setV(f.key, e.target.value)}
                        className="pl-8 h-8 text-sm"
                        data-testid={`verba-${f.key}`}
                      />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-red-700 mb-1.5 uppercase tracking-wide">
                Descontos
              </div>
              <div className="grid grid-cols-2 gap-2">
                {VERBAS_FIELDS.filter((f) => f.group === "descontos").map((f) => (
                  <label key={f.key} className="text-[11px] text-slate-600">
                    {f.label}
                    <div className="relative mt-0.5">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">R$</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0,00"
                        value={verbas[f.key] || ""}
                        onChange={(e) => setV(f.key, e.target.value)}
                        className="pl-8 h-8 text-sm"
                        data-testid={`verba-${f.key}`}
                      />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Total Bruto</span>
                <span className="font-mono font-bold text-emerald-700">
                  R$ {fmtBrl(totals.bruto)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Total Deduções</span>
                <span className="font-mono font-bold text-red-700">
                  R$ {fmtBrl(totals.deducoes)}
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-slate-300">
                <span className="text-slate-800 font-medium">Valor Líquido</span>
                <span className="font-mono font-bold text-blue-800 text-base">
                  R$ {fmtBrl(totals.liquido)}
                </span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-row justify-between">
          {step > 1 ? (
            <Button
              variant="ghost" size="sm"
              onClick={() => setStep((s) => s - 1)}
              className="text-slate-600"
            >
              Voltar
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            {step === 2 && (
              <Button
                onClick={() => {
                  if (!dataDesligamento) {
                    toast.error("Informe a data de desligamento.");
                    return;
                  }
                  setStep(3);
                }}
                disabled={!dataDesligamento}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="termino-create-next"
              >
                Avançar →
              </Button>
            )}
            {step === 3 && (
              <>
                <Button
                  onClick={() => submit({ onlyComunicado: true })}
                  disabled={creating}
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="termino-create-only-comunicado"
                  title="Gera só o Comunicado — TRCT e outros docs serão anexados depois"
                >
                  {creating
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Gerando…</>
                    : <><FileText className="w-3.5 h-3.5 mr-1.5" />Gerar apenas Comunicado</>}
                </Button>
                <Button
                  onClick={() => submit()} disabled={creating}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="termino-create-submit"
                >
                  {creating
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Gerando…</>
                    : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Gerar documentos</>}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ------------------- Main tab component -------------------
export default function AdminCentralDemissionalTab() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [uploadingFor, setUploadingFor] = useState(null);
  const uploadInputRef = useRef(null);
  const [deleting, setDeleting] = useState(null);
  const [kitOpen, setKitOpen] = useState(null);  // record being opened

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/termino-contrato/list`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 20000,
      });
      setRows(r.data?.items || []);
    } catch {
      toast.error("Erro ao carregar comunicados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.employee_nome || "").toUpperCase().includes(q)
      || (r.employee_matricula || "").toUpperCase().includes(q)
      || (r.doc_number || "").toUpperCase().includes(q),
    );
  }, [rows, search]);

  const preview = async (rec) => {
    try {
      const r = await axios.get(
        `${API}/admin/termino-contrato/${rec.id}/preview`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob", timeout: 30000,
        },
      );
      const url = URL.createObjectURL(r.data);
      setPreviewUrl(url);
      setPreviewName(
        `AVI-Termino-${rec.doc_number}-${rec.employee_matricula}.pdf`,
      );
    } catch {
      toast.error("Falha ao carregar PDF.");
    }
  };

  const previewTrct = async (rec) => {
    try {
      const r = await axios.get(
        `${API}/admin/termino-contrato/${rec.id}/trct`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob", timeout: 30000,
        },
      );
      const url = URL.createObjectURL(r.data);
      setPreviewUrl(url);
      setPreviewName(
        `AVI-TRCT-${rec.doc_number}-${rec.employee_matricula}.pdf`,
      );
    } catch {
      toast.error("Falha ao carregar TRCT.");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const startUpload = (rec) => {
    setUploadingFor(rec);
    setTimeout(() => uploadInputRef.current?.click(), 50);
  };

  const uploadSigned = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file || !uploadingFor) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Envie um PDF.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(
        `${API}/admin/termino-contrato/${uploadingFor.id}/upload-signed`,
        fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        });
      toast.success("PDF assinado enviado com sucesso.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload.");
    } finally {
      setUploadingFor(null);
    }
  };

  const removeItem = async (rec) => {
    if (!window.confirm(
      `Excluir o comunicado ${rec.doc_number} de ${rec.employee_nome}?`,
    )) return;
    setDeleting(rec.id);
    try {
      await axios.delete(
        `${API}/admin/termino-contrato/${rec.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Comunicado excluído.");
      setRows((prev) => prev.filter((x) => x.id !== rec.id));
    } catch {
      toast.error("Falha ao excluir.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardContent className="p-4 sm:p-6">
        {/* Header + primary actions */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <UserMinus className="w-5 h-5 text-orange-600" />
            <h2 className="font-heading text-base sm:text-lg font-bold text-slate-800">
              Central Demissional
            </h2>
            <Badge variant="outline" className="text-[10px]">
              {rows.length} comunicado(s)
            </Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="bg-orange-600 hover:bg-orange-700 h-8 text-xs"
              data-testid="termino-btn-create"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Gerar novo comunicado
            </Button>
            <div className="text-[11px] text-slate-500 hidden sm:block">
              ou envie um PDF assinado na linha desejada
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="relative mb-3 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Filtrar por nome, matrícula ou nº do documento…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 h-9"
            data-testid="termino-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              title="Limpar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Carregando…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center">
            <UserMinus className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              Nenhum comunicado de desligamento emitido ainda.
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Clique em <strong>Gerar novo comunicado</strong> para começar.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">
            Nenhum resultado para &quot;{search}&quot;.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 border rounded-md"
              data-testid="termino-list">
            {filtered.map((r) => (
              <li
                key={r.id}
                className="p-3 sm:p-4 flex items-center gap-3 flex-wrap"
                data-testid={`termino-row-${r.doc_number}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">
                      {r.employee_nome}
                    </span>
                    <Badge variant="outline" className="text-[10px] font-mono">
                      {r.doc_number}
                    </Badge>
                    {r.status === "signed" ? (
                      <Badge className="bg-emerald-500 text-white text-[10px]">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Assinado
                      </Badge>
                    ) : r.status === "partial" ? (
                      <Badge className="bg-sky-500 text-white text-[10px]">
                        <Clock className="w-3 h-3 mr-1" /> Parcial
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">
                        <Clock className="w-3 h-3 mr-1" /> Pendente
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 flex gap-3 flex-wrap">
                    <span className="font-mono">Matr. {r.employee_matricula}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Desligamento: <strong>{fmtBr(r.data_desligamento)}</strong>
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      Exame: {fmtBr(r.data_exame)}
                    </span>
                    {r.employee_posto && (
                      <span className="flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3" />
                        {r.employee_posto}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => preview(r)}
                    className="h-8 text-xs"
                    data-testid={`termino-preview-${r.doc_number}`}
                    title="Ver comunicado"
                  >
                    <Eye className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Comunicado</span>
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => previewTrct(r)}
                    className="h-8 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid={`termino-trct-${r.doc_number}`}
                    title="Ver TRCT + Homologação"
                  >
                    <FileText className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">TRCT</span>
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setKitOpen(r)}
                    className="h-8 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid={`termino-kit-${r.doc_number}`}
                    title="Anexar TRCT / outros documentos ao kit demissional"
                  >
                    <Package className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Kit</span>
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => startUpload(r)}
                    className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid={`termino-upload-${r.doc_number}`}
                    title="Enviar PDF assinado"
                  >
                    <Upload className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Assinado</span>
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => removeItem(r)}
                    disabled={deleting === r.id}
                    className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
                    title="Excluir"
                  >
                    {deleting === r.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Hidden file input for uploads */}
        <input
          ref={uploadInputRef}
          type="file" accept="application/pdf"
          className="hidden"
          onChange={uploadSigned}
        />
      </CardContent>

      <CreateTerminoDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />

      <KitDemissionalDialog
        open={!!kitOpen}
        rec={kitOpen ? (rows.find((x) => x.id === kitOpen.id) || kitOpen) : null}
        onClose={() => setKitOpen(null)}
        onUpdated={load}
      />

      {/* Preview dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-5xl w-[95vw] h-[92vh] p-0 flex flex-col">
          <DialogHeader className="p-3 border-b shrink-0">
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="truncate text-sm">{previewName}</span>
              <a
                href={previewUrl || "#"} download={previewName}
                className="text-xs text-blue-600 hover:underline shrink-0"
              >
                Baixar PDF
              </a>
            </DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe
              src={`${previewUrl}#view=FitH&toolbar=1`}
              title="Preview"
              className="w-full flex-1 border-0 bg-slate-100"
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
